import { RHCErrorPage } from "@rhc-shared-components/error-page";
import { type ComponentType, type FC, type LazyExoticComponent, lazy, Suspense } from "react";
import type { match } from "react-router-dom";
import Spinner from "../Spinner/Spinner";

export interface ComponentLoaderProps {
	componentName: EComponentList;
	authenticated?: boolean;
	match?: match;
}

// Lazy loaded component list
export enum EComponentList {
	ProjectList = "ProjectList",
	ProductList = "ProductList",
	ProductGeneralDetails = "ProductGeneralDetails",
	ProductFeatures = "ProductFeatures",
	ProductQuickStart = "ProductQuickStart",
	ProductLinkedResources = "ProductLinkedResources",
	ProductLegalDetails = "ProductLegalDetails",
	ProductSEO = "ProductSEO",
	ProductFAQ = "ProductFAQ",
	ProductContacts = "ProductContacts",
	ProductSupport = "ProductSupport",
	ProductVersionMatrix = "ProductVersionMatrix",
	FourOhFour = "FourOhFour",
}

const FourNotFour = () => {
	return <RHCErrorPage errorStatus={404} />;
};

const lazyComponents: Record<EComponentList, LazyExoticComponent<ComponentType<any>>> = {
	[EComponentList.ProjectList]: lazy(() => import("../../ProjectList/ProjectList")),
	[EComponentList.ProductList]: lazy(() => import("../../ProductList/ProductList")),
	[EComponentList.ProductGeneralDetails]: lazy(
		() => import("../../ProductInfo/ProductGeneralDetails/ProductGeneralDetails"),
	),
	[EComponentList.ProductFeatures]: lazy(() => import("../../ProductInfo/ProductFeatures/ProductFeatures")),
	[EComponentList.ProductQuickStart]: lazy(() => import("../../ProductInfo/ProductQuickStart/ProductQuickStart")),
	[EComponentList.ProductLinkedResources]: lazy(
		() => import("../../ProductInfo/ProductLinkedResources/ProductLinkedResources"),
	),
	[EComponentList.ProductLegalDetails]: lazy(() => import("../../ProductInfo/ProductLegalDetails/ProductLegalDetails")),
	[EComponentList.ProductSEO]: lazy(() => import("../../ProductInfo/ProductSEO/ProductSEO")),
	[EComponentList.ProductFAQ]: lazy(() => import("../../ProductInfo/ProductFAQ/ProductFAQ")),
	[EComponentList.ProductContacts]: lazy(() => import("../../ProductInfo/ProductContacts/ProductContact")),
	[EComponentList.ProductSupport]: lazy(() => import("../../ProductInfo/ProductSupport/ProductSupport")),
	[EComponentList.ProductVersionMatrix]: lazy(
		() => import("../../ProductInfo/ProductVersionMatrix/ProductVersionMatrix"),
	),
	[EComponentList.FourOhFour]: lazy(() => Promise.resolve({ default: FourNotFour })),
};

const ComponentLoader: FC<ComponentLoaderProps> = ({ componentName, match }) => {
	const Component = lazyComponents[componentName] || lazyComponents[EComponentList.FourOhFour];

	return (
		<Suspense fallback={<Spinner />}>
			<Component match={match} />
		</Suspense>
	);
};

export default ComponentLoader;
